import React, { useEffect, useRef, useState, useCallback } from "react";
import Slider, { Settings as SlickSettings } from "react-slick";
import {
  makeStyles,
  createStyles,
  Theme,
  Button,
  useMediaQuery,
  CircularProgress,
  IconButton,
  Typography,
  Box,
} from "@material-ui/core";
import {
  ChevronLeft,
  ChevronRight,
  X,
  Heart,
  MessageCircle,
  Bookmark,
  DollarSign,
  Share,
} from "lucide-react";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { formatPostDate } from "../../utils/timeFormatter";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    sliderRoot: {
      position: "relative",
      width: "100%",
      height: (props: { height: string }) => props.height,
      overflow: "hidden",
      "& .slick-list": {
        height: (props: { height: string }) => props.height,
        overflow: "hidden",
        position: "relative",
        width: "100%",
        "& .slick-track": {
          width: "100% !important",
        },
      },
      "& .slick-track": {
        border: "none",
        height: (props: { height: string }) => props.height,
        display: "flex",
        alignItems: "stretch",
        "& .slick-slide": {
          position: "relative",
          flexBasis: "100% !important",
          width: "100% !important",
          minWidth: "100% !important",
          maxWidth: "100% !important",
        },
      },
      "& .slick-slide": {
        border: "none",
        height: (props: { height: string }) => props.height,
        float: "left",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100% !important",
        minWidth: "100% !important",
        maxWidth: "100% !important",
        flexShrink: 0,
        "& > div": {
          height: "100%",
          width: "100%",
          display: "block",
          "& > div": {
            height: "-webkit-fill-available",
          },
        },
        "&[aria-hidden='true']": {
          visibility: "hidden",
        },
      },
      [theme.breakpoints.down("xs")]: {
        marginTop: 0,
        marginBottom: 0,
      },
    },
    slideMedia: {
      width: "100%",
      height: "auto",
      minWidth: "100%",
      maxWidth: "100%",
      minHeight: "400px",
      maxHeight: "85vh",
      objectFit: "contain",
      objectPosition: "center center",
      display: "block",
      margin: "0 auto",
      backgroundColor: "#000",
      borderRadius: 16,
      [theme.breakpoints.down("xs")]: {
        marginTop: 0,
        marginBottom: 0,
        width: "100%",
        height: "auto",
      },
    },
    loadingContainer: {
      width: "100%",
      height: (props: { height: string }) => props.height,
      minHeight: (props: { height: string }) => props.height,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#000",
    },
    dotsContainer: {
      display: "flex",
      justifyContent: "center",
      marginTop: theme.spacing(1),
      userSelect: "none",
      [theme.breakpoints.down("xs")]: {
        marginTop: theme.spacing(0.5),
        marginBottom: 0,
      },
    },
    dot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      backgroundColor: "#ccc",
      margin: "0 4px",
      cursor: "pointer",
      transition: "background-color .3s",
    },
    dotActive: {
      backgroundColor: theme.palette.primary.main,
    },
    prevArrow: {
      position: "absolute",
      top: "50%",
      left: theme.spacing(2),
      transform: "translateY(-50%)",
      zIndex: 10,
      width: 40,
      height: 40,
      borderRadius: "50%",
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      [theme.breakpoints.down("xs")]: {
        width: 30,
        height: 30,
        left: theme.spacing(1),
      },
    },
    nextArrow: {
      position: "absolute",
      top: "50%",
      right: theme.spacing(2),
      transform: "translateY(-50%)",
      zIndex: 10,
      width: 40,
      height: 40,
      borderRadius: "50%",
      backgroundColor: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      [theme.breakpoints.down("xs")]: {
        width: 30,
        height: 30,
        right: theme.spacing(1),
      },
    },
    purchaseButton: {
      position: "absolute",
      left: "50%",
      bottom: "50%",
      transform: "translate(-50%, 50%)",
      zIndex: 2,
      color: "#FE5A97",
      border: "1px solid #FE5A97",
      borderRadius: "5px",
      fontStyle: "normal",
      fontWeight: 800,
      fontSize: "14px",
      textTransform: "none",
      padding: "10px 20px",
      [theme.breakpoints.down("xs")]: {
        fontSize: "12px",
        padding: "8px 16px",
        width: "80%",
      },
    },
    unlockButton: {
      backgroundColor: "#FE5A97",
      color: "#fff",
      borderRadius: "4px",
      padding: "8px 16px",
      fontWeight: "bold",
      textTransform: "none",
      "&:hover": {
        backgroundColor: "#e04a82",
      },
    },
    errorContainer: {
      width: "100%",
      height: (props: { height: string }) => props.height,
      minHeight: (props: { height: string }) => props.height,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#000",
      color: "#fff",
      fontSize: "14px",
      textAlign: "center",
    },
    closeIcon: {
      position: "absolute",
      top: 40,
      right: 16,
      zIndex: 10,
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      borderRadius: "50%",
      padding: "8px",
      cursor: "pointer",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      },
    },
    bottomIconsContainer: {
      position: "absolute",
      bottom: 16,
      right: 16,
      zIndex: 10,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 12,
    },
    iconButton: {
      backgroundColor: "rgba(0, 0, 0, 0.5)",
      borderRadius: "50%",
      padding: "8px",
      cursor: "pointer",
      color: "#fff",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.7)",
      },
    },
    overlayContent: {
      position: "absolute",
      left: 16,
      bottom: 16,
      right: 16,
      padding: "8px 12px",
      zIndex: 5,
    },
    overlayUsername: {
      color: "#FFFFFF",
      fontWeight: 600,
      fontSize: 24,
      marginBottom: 4,
    },
    overlayDescription: {
      color: "#FFFFFF",
      fontSize: 20,
      fontWeight: 600,
      marginBottom: 4,
    },
    overlayHashtags: {
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
    },
    overlayHashtag: {
      fontSize: 18,
      fontWeight: 400,
      color: "#FFFFFF",
    },
  })
);

interface ArrowProps {
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

const PrevArrow: React.FC<ArrowProps> = ({ onClick }) => {
  const classes = useStyles({ height: "400px" });
  return (
    <div className={classes.prevArrow} onClick={onClick}>
      <ChevronLeft size={24} color="#000" />
    </div>
  );
};

const NextArrow: React.FC<ArrowProps> = ({ onClick }) => {
  const classes = useStyles({ height: "400px" });
  return (
    <div className={classes.nextArrow} onClick={onClick}>
      <ChevronRight size={24} color="#000" />
    </div>
  );
};

export interface SliderItem {
  id: string;
  originalMediaUrl: string;
  mediaUrl: string;
  type: "image" | "video";
  isBlurred?: boolean;
}

export interface UniversalSliderProps {
  items: SliderItem[];
  shouldPlayVideo: boolean;
  height?: number | string;
  settings?: Partial<SlickSettings>;
  className?: string;
  onDoubleClick?: () => void;
  price?: number;
  loadingMedia?: { [key: string]: boolean };
  onPurchaseClick: () => void;
  onClose?: () => void;
  onLike?: () => void;
  onComment?: () => void;
  onBookmark?: () => void;
  onTip?: () => void;
  onShare?: () => void;
  username?: string;
  description?: string;
  hashtags?: string[];
  createdAt?: string;
  isLiked?: boolean;
  showComments?: boolean;
  postOwnerId?: string;
  currentUserId?: string;
  /** When true, no-save protection is disabled (e.g. user has premium subscription). */
  hasPremiumSubscription?: boolean;
}

export const UniversalSlider: React.FC<UniversalSliderProps> = ({
  items,
  shouldPlayVideo,
  height = 400,
  settings,
  className,
  onDoubleClick,
  price,
  loadingMedia = {},
  onPurchaseClick,
  onClose,
  onLike,
  onComment,
  onBookmark,
  onTip,
  onShare,
  username,
  description,
  hashtags,
  createdAt,
  isLiked = false,
  showComments = false,
  postOwnerId,
  currentUserId,
  hasPremiumSubscription = false,
}) => {
  const classes = useStyles({
    height: typeof height === "number" ? `${height}px` : height,
  });
  const sliderRef = useRef<Slider>(null);
  const [current, setCurrent] = useState(0);
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("xs")
  );
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);
  const [errorItems, setErrorItems] = useState<Set<string>>(new Set());
  const defaultSettings: SlickSettings = {
    dots: false,
    infinite: items.length > 1,
    speed: 300,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: !isMobile || items.length > 1,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    centerMode: false,
    variableWidth: false,
    initialSlide: 0,
    beforeChange: (_: number, next: number) => setCurrent(next),
    adaptiveHeight: false,
    lazyLoad: "ondemand" as const,
  };

  const cfg = { ...defaultSettings, ...settings };

  useEffect(() => {
    // Ensure slider starts at first slide
    if (sliderRef.current) {
      sliderRef.current.slickGoTo(0, false);
    }
  }, [items.length]);

  const handleSelectVideo = useCallback(
    (item: SliderItem) => {
      if (item.type === "video" && shouldPlayVideo) {
        setSelectedVideo(item.id);
      }
    },
    [shouldPlayVideo]
  );

  const handleError = useCallback((itemId: string) => {
    setErrorItems(prev => {
      const newSet = new Set(prev);
      newSet.add(itemId);
      return newSet;
    });
  }, []);

  const getMediaTypeText = (type: string) =>
    type === "video" ? "Video" : "Image";

  return (
    <div className={`${classes.sliderRoot} ${className || ""}`}>
      <Slider ref={sliderRef} {...cfg}>
        {items.map(item => {
          const isProtectedMedia =
            !hasPremiumSubscription &&
            (!!item.isBlurred ||
              (price != null &&
                price > 0 &&
                (!postOwnerId ||
                  !currentUserId ||
                  currentUserId !== postOwnerId)));
          return (
            <div
              key={item.id}
              onDoubleClick={onDoubleClick}
              onMouseEnter={() => handleSelectVideo(item)}
              onMouseOver={() => handleSelectVideo(item)}
              onMouseLeave={() => setSelectedVideo(null)}
              style={{
                width: "100%",
                height: typeof height === "number" ? `${height}px` : height,
                overflow: "hidden",
                display: "block",
                position: "relative",
              }}
            >
              {loadingMedia[item.originalMediaUrl] ? (
                <div className={classes.loadingContainer}>
                  <CircularProgress size={48} />
                </div>
              ) : errorItems.has(item.id) ? (
                <div className={classes.errorContainer}>
                  Failed to load {getMediaTypeText(item.type)}
                </div>
              ) : item.type === "video" &&
                shouldPlayVideo &&
                !item.isBlurred ? (
                // Показуємо відео тільки якщо є доступ і воно не заблюрене
                <video
                  src={item.mediaUrl}
                  controls={false}
                  autoPlay
                  muted={selectedVideo !== item.id}
                  loop
                  className={classes.slideMedia}
                  playsInline
                  onError={() => handleError(item.id)}
                  controlsList="nodownload nofullscreen noremoteplayback"
                  disablePictureInPicture
                  onContextMenu={
                    !hasPremiumSubscription
                      ? (e: React.MouseEvent) => e.preventDefault()
                      : undefined
                  }
                />
              ) : item.type === "video" ? (
                // Для відео без доступу або з isBlurred бекенд вже надає прев'ю через mediaUrl, показуємо як зображення
                <img
                  src={item.mediaUrl}
                  alt={`slide-${item.id}`}
                  className={classes.slideMedia}
                  loading="lazy"
                  onError={() => handleError(item.id)}
                  onContextMenu={
                    isProtectedMedia
                      ? (e: React.MouseEvent) => e.preventDefault()
                      : undefined
                  }
                  onDragStart={
                    isProtectedMedia
                      ? (e: React.DragEvent) => e.preventDefault()
                      : undefined
                  }
                  draggable={isProtectedMedia ? false : undefined}
                />
              ) : (
                <img
                  src={item.mediaUrl}
                  alt={`slide-${item.id}`}
                  className={classes.slideMedia}
                  loading="lazy"
                  onError={() => handleError(item.id)}
                  onContextMenu={
                    isProtectedMedia
                      ? (e: React.MouseEvent) => e.preventDefault()
                      : undefined
                  }
                  onDragStart={
                    isProtectedMedia
                      ? (e: React.DragEvent) => e.preventDefault()
                      : undefined
                  }
                  draggable={isProtectedMedia ? false : undefined}
                />
              )}

              {!shouldPlayVideo &&
                price &&
                price > 0 &&
                (!postOwnerId ||
                  !currentUserId ||
                  currentUserId !== postOwnerId) && (
                  <Button
                    className={classes.purchaseButton}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.stopPropagation();
                      onPurchaseClick();
                    }}
                  >
                    ${price} Purchase {getMediaTypeText(item.type)}
                  </Button>
                )}

              {item.isBlurred && shouldPlayVideo && (
                <Button
                  className={classes.unlockButton}
                  style={{
                    position: "absolute",
                    bottom: "20%",
                    left: "50%",
                    transform: "translateX(-50%)",
                    zIndex: 2,
                  }}
                >
                  Follow to Unlock
                </Button>
              )}

              {/* Close modal — top right (media) */}
              {onClose && !isMobile && (
                <IconButton
                  className={classes.closeIcon}
                  onClick={e => {
                    e.stopPropagation();
                    onClose();
                  }}
                  aria-label="close"
                  style={{ color: "#fff" }}
                >
                  <X size={20} color="#fff" />
                </IconButton>
              )}

              {/* Bottom right icons */}
              {!isMobile && (
                <div className={classes.bottomIconsContainer}>
                  {onLike && (
                    <IconButton
                      className={classes.iconButton}
                      onClick={e => {
                        e.stopPropagation();
                        onLike();
                      }}
                    >
                      <Heart
                        size={20}
                        color={isLiked ? "#FE5A97" : "#fff"}
                        fill={isLiked ? "#FE5A97" : "none"}
                      />
                    </IconButton>
                  )}
                  {onComment && (
                    <IconButton
                      className={classes.iconButton}
                      onClick={e => {
                        e.stopPropagation();
                        onComment();
                      }}
                      style={{ marginBottom: 43 }}
                    >
                      <MessageCircle
                        size={20}
                        color={showComments ? "#FE5A97" : "#fff"}
                      />
                    </IconButton>
                  )}
                  {onBookmark && (
                    <IconButton
                      className={classes.iconButton}
                      onClick={e => {
                        e.stopPropagation();
                        onBookmark();
                      }}
                    >
                      <Bookmark size={20} color="#fff" />
                    </IconButton>
                  )}
                  {onTip && (
                    <IconButton
                      className={classes.iconButton}
                      onClick={e => {
                        e.stopPropagation();
                        onTip();
                      }}
                    >
                      <DollarSign size={20} color="#fff" />
                    </IconButton>
                  )}
                  {onShare && (
                    <IconButton
                      className={classes.iconButton}
                      onClick={e => {
                        e.stopPropagation();
                        onShare();
                      }}
                    >
                      <Share size={20} color="#fff" />
                    </IconButton>
                  )}
                </div>
              )}

              {/* Overlay with username, description and hashtags */}
              {/* {!isMobile &&
                (username ||
                  description ||
                  (hashtags && hashtags.length > 0)) && (
                  <Box className={classes.overlayContent}>
                    {username && (
                      <Typography className={classes.overlayUsername}>
                        @{username}
                      </Typography>
                    )}
                    {description && (
                      <Typography className={classes.overlayDescription}>
                        {description}
                      </Typography>
                    )}
                    {hashtags && hashtags.length > 0 && (
                      <Box className={classes.overlayHashtags}>
                        {hashtags.map((tag, index) => (
                          <Typography
                            key={index}
                            className={classes.overlayHashtag}
                          >
                            #{tag}
                          </Typography>
                        ))}
                      </Box>
                    )}
                    {createdAt && (
                      <Typography
                        style={{
                          color: "#FFFFFFCC",
                          fontSize: "20px",
                          fontWeight: 400,
                          marginTop: "8px",
                          textAlign: "left",
                        }}
                      >
                        {formatPostDate(createdAt)}
                      </Typography>
                    )}
                  </Box>
                )} */}
            </div>
          );
        })}
      </Slider>

      <div className={classes.dotsContainer}>
        {items.map((_, idx) => (
          <span
            key={idx}
            className={`${classes.dot} ${idx === current ? classes.dotActive : ""
              }`}
            onClick={() => sliderRef.current?.slickGoTo(idx)}
          />
        ))}
      </div>
    </div>
  );
};

export default UniversalSlider;
